import { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, View, Text } from "react-native";
import {
  ClipboardList,
  FlaskConical,
  Pill,
  CalendarDays,
  CalendarCheck,
  FileText,
  Trash2,
} from "lucide-react-native";
import {
  ActionBanner,
  Dialog,
  DialogActions,
  DialogBodyText,
  DialogIcon,
  DialogTitleText,
  PrimaryButton,
  MoonToggle,
  Row,
  SecondaryButton,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../../onboarding/theme";
import StatusBadge from "../../../components/StatusBadge";
import { useCarePersons } from "@src/packages/patients/hooks/useCarePersons";
import {
  useCancelPartnerInvitation,
  usePartnerInvitation,
  useSendPartnerInvite,
  useUnlinkPartner,
} from "@src/packages/patients/hooks/usePartnerInvite";
import { usePartnerSharingScope } from "@src/packages/patients/hooks/usePartnerSharingScope";
import type { SharingCategory } from "@src/packages/patients/hooks/usePatientSharing";
import type {
  ConnectionType,
  ConnectionWithSharing,
  DataPermissionKey,
  YouScreenAction,
} from "../types";
import { useActiveCycle } from "@src/packages/cycles/hooks/useCycles";
import {
  useSharingPermissions,
  useUpdatePatientSharing,
  PERMISSION_TO_CATEGORY,
} from "@src/packages/patients/hooks/usePatientSharing";
import { usePatientSyncStatus } from "@src/packages/patients/sync/usePatientSync";
import { GroupedCard } from "../components";
import {
  blocksMedicalHistorySharingGrant,
  hasPendingMedicalHistoryWrites,
} from "./dataSharingSyncPolicy";
import { semanticColors } from "@louise/theme/tokens";

const padlockIcon =
  require("../../../../assets/images/you-icons/padlock.png") as number;
const trashIcon =
  require("../../../../assets/images/you-icons/trash-bin.png") as number;

const PERMISSION_META: Record<
  DataPermissionKey,
  { key: string; Icon: typeof ClipboardList }
> = {
  medicalHistory: { key: "medical_history", Icon: ClipboardList },
  testResults: { key: "test_results", Icon: FlaskConical },
  medications: { key: "medications", Icon: Pill },
  cycleData: { key: "cycle_data", Icon: CalendarDays },
  appointments: { key: "appointments", Icon: CalendarCheck },
  documents: { key: "documents", Icon: FileText },
};

const DEFAULT_PENDING_PERMISSIONS = {
  medicalHistory: false,
  testResults: false,
  medications: false,
  cycleData: false,
  appointments: false,
  documents: false,
};

function permissionsFromInvitation(
  categories: SharingCategory[],
): Record<DataPermissionKey, boolean> {
  return {
    medicalHistory: categories.includes("history"),
    testResults: categories.includes("test_results"),
    medications: categories.includes("medications"),
    cycleData: categories.includes("cycles"),
    appointments: categories.includes("appointments"),
    documents: categories.includes("documents"),
  };
}

function categoriesFromPermissions(
  permissions: Record<DataPermissionKey, boolean>,
  existingCategories: SharingCategory[],
): SharingCategory[] {
  const managedCategories = new Set<SharingCategory>([
    "history",
    "test_results",
    "medications",
    "cycles",
    "appointments",
    "documents",
  ]);
  const categories = existingCategories.filter(
    (category) => !managedCategories.has(category),
  );
  if (permissions.medicalHistory) categories.push("history");
  if (permissions.testResults) categories.push("test_results");
  if (permissions.medications) categories.push("medications");
  if (permissions.cycleData) categories.push("cycles");
  if (permissions.appointments) categories.push("appointments");
  if (permissions.documents) categories.push("documents");
  return categories;
}

function formatInviteDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

type Props = {
  visible: boolean;
  connectionType: ConnectionType | null;
  connections: ConnectionWithSharing[];
  dispatch: React.Dispatch<YouScreenAction>;
  onClose: () => void;
};

export default function DataSharingModal({
  visible,
  connectionType,
  connections,
  dispatch,
  onClose,
}: Props) {
  const t = useTranslations();

  const { data: activeCycle } = useActiveCycle();
  const { partnerId, partnerName, partnerPatient, partnerConnected } =
    useCarePersons();
  const { data: invitation } = usePartnerInvitation();
  const { scope: partnerSharingScope } = usePartnerSharingScope();
  const updateSharing = useUpdatePatientSharing();
  const sendPartnerInvite = useSendPartnerInvite();
  const unlinkMutation = useUnlinkPartner();
  const cancelInvitationMutation = useCancelPartnerInvitation();
  const patientSync = usePatientSyncStatus();
  const [removeConfirmationVisible, setRemoveConfirmationVisible] =
    useState(false);
  const [pendingPermissions, setPendingPermissions] = useState(
    DEFAULT_PENDING_PERMISSIONS,
  );
  const [pendingInviteSaveRequested, setPendingInviteSaveRequested] =
    useState(false);
  const [pendingInviteSaved, setPendingInviteSaved] = useState(false);
  const hasLocalMedicalHistory = hasPendingMedicalHistoryWrites(patientSync);

  const targetId = useMemo(() => {
    if (connectionType === "partner") return partnerId;
    if (connectionType === "clinic") return activeCycle?.providerId ?? null;
    return null;
  }, [connectionType, activeCycle?.providerId, partnerId]);

  const apiPermissions = useSharingPermissions(targetId);
  const isApiConnected = Boolean(targetId);

  const realConnection = connections.find((c) => c.type === connectionType);
  const isPendingInvite =
    connectionType === "partner" &&
    invitation?.status === "pending" &&
    !partnerConnected;
  const hasConnection =
    isApiConnected ||
    (connectionType === "partner" && partnerConnected) ||
    realConnection?.status === "connected";
  const isPending = isPendingInvite || !hasConnection;

  useEffect(() => {
    if (!isPendingInvite || !invitation) return;
    setPendingPermissions(
      permissionsFromInvitation(invitation.sharingCategories),
    );
    setPendingInviteSaveRequested(false);
    setPendingInviteSaved(false);
  }, [invitation, isPendingInvite]);

  useEffect(() => {
    if (pendingInviteSaveRequested && sendPartnerInvite.isSuccess) {
      setPendingInviteSaved(true);
    }
  }, [pendingInviteSaveRequested, sendPartnerInvite.isSuccess]);

  if (!connectionType) return null;

  const connectionName = isApiConnected
    ? connectionType === "partner"
      ? partnerName
      : (activeCycle?.provider ?? null)
    : isPendingInvite
      ? (invitation?.inviteeFirstName ?? realConnection?.name ?? null)
      : (realConnection?.name ?? null);

  const permissions = isPendingInvite
    ? pendingPermissions
    : isApiConnected && apiPermissions
      ? apiPermissions
      : DEFAULT_PENDING_PERMISSIONS;
  const canChangePermissions =
    (isPendingInvite && Boolean(invitation)) ||
    (isApiConnected && Boolean(targetId && apiPermissions));
  const canRemovePartner =
    connectionType === "partner" && partnerConnected && !isPending;
  const removalPending =
    unlinkMutation.isPending || cancelInvitationMutation.isPending;
  const partnerContact = [
    isPendingInvite
      ? (invitation?.inviteeFirstName ??
        t("you.connections_sharing.partner_label"))
      : (partnerName ?? t("you.connections_sharing.partner_label")),
    isPendingInvite
      ? (invitation?.inviteePhone ??
        t("you.modals.data_sharing.partner_phone_unavailable"))
      : (partnerPatient?.phoneNumber ??
        t("you.modals.data_sharing.partner_phone_unavailable")),
  ].join(" · ");
  const removeTitle = isPendingInvite
    ? t("you.modals.partner_manage.cancel_invitation_title")
    : t("you.modals.partner_manage.remove_partner_connection_title");
  const removeMessage = isPendingInvite
    ? t("you.modals.partner_manage.cancel_invitation_message")
    : t("you.modals.partner_manage.remove_partner_connection_message");
  const removeConfirmLabel = isPendingInvite
    ? t("you.modals.partner_manage.confirm_invitation_cancellation")
    : t("you.modals.partner_manage.remove_connection");
  const pendingStatusLabel =
    isPendingInvite && invitation
      ? t("you.modals.data_sharing.pending_invite_status_with_expiry", {
          params: { date: formatInviteDate(invitation.expiresAt) },
        })
      : t("you.modals.data_sharing.pending_invite_status_value");
  const pendingRecipientLabel = isPendingInvite
    ? t("you.modals.data_sharing.pending_invite_sent_to", {
        params: {
          phone:
            invitation?.inviteePhone ??
            t("you.modals.data_sharing.partner_phone_unavailable"),
        },
      })
    : null;

  const inviteScope =
    invitation?.sharingCaseId && invitation.sharingCycleId
      ? {
          caseId: invitation.sharingCaseId,
          cycleId: invitation.sharingCycleId,
        }
      : partnerSharingScope;

  const performRemove = async () => {
    try {
      if (isPendingInvite) {
        await cancelInvitationMutation.mutateAsync();
      } else {
        await unlinkMutation.mutateAsync();
      }
      setRemoveConfirmationVisible(false);
      dispatch({ type: "REMOVE_PARTNER" });
      onClose();
    } catch {
      // Error shown inline
    }
  };

  const handleRemove = () => setRemoveConfirmationVisible(true);

  const savePendingInvite = () => {
    if (!isPendingInvite || !invitation || !inviteScope) return;
    setPendingInviteSaveRequested(true);
    setPendingInviteSaved(false);
    sendPartnerInvite.mutate({
      phone: invitation.inviteePhone,
      firstName: invitation.inviteeFirstName ?? undefined,
      sharingCategories: categoriesFromPermissions(
        pendingPermissions,
        invitation.sharingCategories,
      ),
      sharingScope: inviteScope,
      replaceExisting: true,
    });
  };

  const handleToggle = (key: DataPermissionKey) => {
    if (
      blocksMedicalHistorySharingGrant({
        key,
        currentlyEnabled: permissions[key],
        hasLocalMedicalHistory,
      })
    ) {
      return;
    }
    if (isApiConnected && targetId && apiPermissions) {
      const newValue = !permissions[key];
      const category = PERMISSION_TO_CATEGORY[key];
      updateSharing.mutate([
        {
          targetId,
          targetType: connectionType === "partner" ? "partner" : "provider",
          category,
          enabled: newValue,
        },
      ]);
      return;
    }
    if (isPendingInvite) {
      setPendingPermissions((current) => ({
        ...current,
        [key]: !current[key],
      }));
      setPendingInviteSaved(false);
      return;
    }
    if (isPending) {
      // Nothing persists until the connection is live — the toggles are
      // rendered disabled in this state (#1138), so this is unreachable,
      // but keep the guard so a future enable can't silently no-op.
      return;
    }
    dispatch({
      type: "TOGGLE_DATA_PERMISSION",
      connectionType,
      permission: key,
    });
  };

  const title = isPending
    ? isPendingInvite
      ? t("you.modals.data_sharing.pending_invitation_for", {
          params: {
            name: connectionName ?? t("you.connections_sharing.partner_label"),
          },
        })
      : t(`you.modals.data_sharing.connect_${connectionType}`)
    : connectionName
      ? t("you.modals.data_sharing.sharing_with", {
          params: { name: connectionName },
        })
      : t(`you.modals.data_sharing.generic_${connectionType}`);

  return (
    <>
      <Dialog
        visible={visible && !removeConfirmationVisible}
        onClose={onClose}
        layout="centered"
        closeButton="minimal"
      >
        <DialogIcon source={padlockIcon} width={80} height={88} />

        <View
          style={{
            alignItems: "center",
            justifyContent: "center",
            gap: isPending ? 8 : 0,
            marginTop: 14,
            marginBottom: isPending ? 8 : 18,
          }}
        >
          <DialogTitleText style={{ textAlign: "center" }}>
            {title}
          </DialogTitleText>
          {isPending && (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <StatusBadge label={pendingStatusLabel} tone="gold" />
              {pendingRecipientLabel && (
                <StatusBadge label={pendingRecipientLabel} tone="neutral" />
              )}
            </View>
          )}
        </View>

        {isPending && !isPendingInvite && (
          <DialogBodyText
            style={{
              textAlign: "center",
              marginTop: 0,
              marginBottom: 18,
              paddingHorizontal: 4,
            }}
          >
            {t("you.modals.data_sharing.pending_intro")}
          </DialogBodyText>
        )}

        {hasLocalMedicalHistory ? (
          <View
            accessibilityLiveRegion="polite"
            style={{ marginBottom: 18, paddingHorizontal: 4 }}
          >
            <DialogBodyText style={{ textAlign: "center", marginTop: 0 }}>
              {t("you.modals.data_sharing.local_history_notice")}
            </DialogBodyText>
          </View>
        ) : null}

        {canRemovePartner && (
          <GroupedCard style={{ marginBottom: 12 }}>
            <Row
              testID="data-sharing-remove-partner"
              surface="plain"
              leading={
                removalPending ? (
                  <ActivityIndicator size={18} color={EMBER.colors.coral} />
                ) : (
                  <Trash2
                    size={18}
                    color={EMBER.colors.coral}
                    strokeWidth={1.8}
                  />
                )
              }
              leadingSize={28}
              title={t("you.modals.data_sharing.remove_partner")}
              subtitle={partnerContact}
              onPress={handleRemove}
              action="you.modals.data_sharing.remove_partner"
              disabled={removalPending}
              chevron={false}
              accessibilityLabel={`${t("you.modals.data_sharing.remove_partner")}. ${partnerContact}`}
              gap={14}
              paddingHorizontal={16}
              paddingVertical={14}
              borderRadius={0}
              titleStyle={{
                fontSize: 15,
                lineHeight: 20,
                fontWeight: "500",
                color: EMBER.colors.coral,
              }}
              subtitleStyle={{
                fontSize: 12,
                lineHeight: 16,
                color: EMBER.colors.textLight,
              }}
              style={{ borderRadius: 0 }}
            />
          </GroupedCard>
        )}

        <View style={{ marginBottom: 20 }}>
          {(Object.keys(PERMISSION_META) as DataPermissionKey[]).map(
            (key, i) => {
              const { key: tKey, Icon } = PERMISSION_META[key];
              const label = t(`you.modals.data_sharing.${tKey}_label`);
              const description = t(
                `you.modals.data_sharing.${tKey}_description`,
              );
              return (
                <View
                  key={key}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    paddingVertical: 12,
                    borderTopWidth: i > 0 ? 1 : 0,
                    borderTopColor: semanticColors.border.subtle,
                    gap: 12,
                  }}
                >
                  <Icon
                    size={18}
                    color={EMBER.colors.textMid}
                    strokeWidth={2}
                  />
                  <View style={{ flex: 1 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        fontWeight: "500",
                        color: EMBER.colors.text,
                      }}
                    >
                      {label}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 11,
                        color: EMBER.colors.textFaint,
                        marginTop: 1,
                      }}
                    >
                      {description}
                    </Text>
                  </View>
                  <MoonToggle
                    value={permissions[key]}
                    onChange={() => handleToggle(key)}
                    accessibilityLabel={label}
                    disabled={
                      !canChangePermissions ||
                      updateSharing.isPending ||
                      (isPending && !isPendingInvite) ||
                      sendPartnerInvite.isPending ||
                      blocksMedicalHistorySharingGrant({
                        key,
                        currentlyEnabled: permissions[key],
                        hasLocalMedicalHistory,
                      })
                    }
                  />
                </View>
              );
            },
          )}
        </View>

        {unlinkMutation.isError && (
          <ActionBanner
            variant="important"
            message={
              unlinkMutation.error?.message ??
              t("you.modals.data_sharing.remove_failed")
            }
            style={{ marginTop: -12, marginBottom: 12 }}
          />
        )}

        {isPendingInvite && sendPartnerInvite.isError && (
          <ActionBanner
            variant="important"
            message={
              sendPartnerInvite.error?.message ??
              t("you.modals.data_sharing.pending_sharing_update_failed")
            }
            style={{ marginTop: -12, marginBottom: 12 }}
          />
        )}

        {isPendingInvite && pendingInviteSaved && (
          <Text
            className="font-dm-sans"
            accessibilityLiveRegion="polite"
            style={{
              fontSize: 12,
              color: EMBER.colors.warm,
              textAlign: "center",
              marginTop: -12,
              marginBottom: 12,
            }}
          >
            {t("you.modals.data_sharing.pending_sharing_updated")}
          </Text>
        )}

        {isPendingInvite ? (
          <DialogActions style={{ marginTop: 0 }}>
            <SecondaryButton
              label={t("you.modals.data_sharing.cancel_invitation")}
              onPress={handleRemove}
              disabled={sendPartnerInvite.isPending || removalPending}
              flex={1}
            />
            <PrimaryButton
              label={t("you.modals.data_sharing.save_invitation")}
              onPress={savePendingInvite}
              disabled={
                sendPartnerInvite.isPending || removalPending || !inviteScope
              }
              loading={sendPartnerInvite.isPending}
              flex={1}
            />
          </DialogActions>
        ) : (
          <PrimaryButton
            label={t("you.modals.data_sharing.done")}
            onPress={onClose}
          />
        )}
      </Dialog>

      <Dialog
        visible={visible && removeConfirmationVisible}
        onClose={() => setRemoveConfirmationVisible(false)}
        layout="centered"
        contentStyle={{ paddingTop: 28, paddingBottom: 24 }}
        testID="data-sharing-remove-confirmation"
      >
        <DialogIcon
          testID="data-sharing-remove-confirmation-illustration"
          source={trashIcon}
        />
        <View accessibilityRole="header" style={{ marginTop: 14 }}>
          <DialogTitleText style={{ textAlign: "center" }}>
            {removeTitle}
          </DialogTitleText>
        </View>
        <DialogBodyText style={{ textAlign: "center", marginBottom: 16 }}>
          {removeMessage}
        </DialogBodyText>
        {(unlinkMutation.isError || cancelInvitationMutation.isError) && (
          <ActionBanner
            variant="important"
            message={
              (isPendingInvite
                ? cancelInvitationMutation.error?.message
                : unlinkMutation.error?.message) ??
              t("you.modals.data_sharing.remove_failed")
            }
            style={{ marginBottom: 12 }}
          />
        )}
        <DialogActions style={{ marginTop: 0 }}>
          <SecondaryButton
            label={t("you.modals.partner_manage.cancel")}
            onPress={() => setRemoveConfirmationVisible(false)}
            disabled={removalPending}
            flex={1}
          />
          <SecondaryButton
            variant="destructive"
            label={removeConfirmLabel}
            onPress={() => void performRemove()}
            loading={removalPending}
            flex={1}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
